import { useState, useCallback } from 'react';

export type ColumnId =
  | 'user'
  | 'role'
  | 'pharmacyNum'
  | 'region'
  | 'phone'
  | 'workPhone'
  | 'fullName'
  | 'iin'
  | 'mail'
  | 'address'
  | 'status'
  | 'posStatus'
  | 'reportsStatus'
  | 'mailSession'
  | 'geolocation'
  | 'geoUpdatedAt'
  | 'belongsStorage'
  | 'id'
  | 'actions';

export interface ColumnDef {
  id: ColumnId;
  label: string;
  visible: boolean;
  fixed?: 'start_1' | 'start_2' | 'end';
  width?: string | number;
  minWidth?: string | number;
  align?: 'left' | 'center' | 'right';
  description?: string;
}

export const STORAGE_KEY = 'zerde_all_users_table_columns_v1';

export const DEFAULT_COLUMNS: ColumnDef[] = [
  {
    id: 'user',
    label: 'Пользователь',
    visible: true,
    fixed: 'start_1',
    width: '220px',
    align: 'left',
    description: 'Логин, аватар и почта'
  },
  {
    id: 'role',
    label: 'Роль',
    visible: true,
    fixed: 'start_2',
    width: '130px',
    align: 'left',
    description: 'Уровень доступа сотрудника'
  },
  {
    id: 'pharmacyNum',
    label: '№ аптеки',
    visible: true,
    width: '90px',
    align: 'left',
    description: 'Номер торговой точки'
  },
  {
    id: 'region',
    label: 'Регион',
    visible: true,
    width: '130px',
    align: 'left',
    description: 'Город или филиал сети'
  },
  {
    id: 'phone',
    label: 'Личный телефон',
    visible: true,
    width: '170px',
    align: 'left',
    description: 'Основной сотовый номер (+7)'
  },
  {
    id: 'workPhone',
    label: 'Рабочий телефон',
    visible: false,
    width: '170px',
    align: 'left',
    description: 'Служебный телефон сотрудника'
  },
  {
    id: 'fullName',
    label: 'ФИО',
    visible: true,
    minWidth: '160px',
    align: 'left',
    description: 'Полное имя сотрудника'
  },
  {
    id: 'iin',
    label: 'ИИН',
    visible: true,
    width: '130px',
    align: 'left',
    description: 'Индивидуальный номер'
  },
  {
    id: 'mail',
    label: 'Корп. почта',
    visible: false,
    width: '170px',
    align: 'left',
    description: 'Корпоративный ящик Carbonio'
  },
  {
    id: 'address',
    label: 'Адрес аптеки',
    visible: false,
    width: '200px',
    align: 'left',
    description: 'Фактический адрес аптеки'
  },
  {
    id: 'status',
    label: 'Статус аккаунта',
    visible: false,
    width: '130px',
    align: 'left',
    description: 'Активен или деактивирован'
  },
  {
    id: 'posStatus',
    label: 'Доступ к кассе',
    visible: false,
    width: '125px',
    align: 'left',
    description: 'Статус блокировки кассы'
  },
  {
    id: 'reportsStatus',
    label: 'Доступ к отчетам',
    visible: false,
    width: '135px',
    align: 'left',
    description: 'Статус блокировки отчетов'
  },
  {
    id: 'mailSession',
    label: 'Почтовая сессия',
    visible: false,
    width: '135px',
    align: 'left',
    description: 'Активность входа в веб-почту'
  },
  {
    id: 'geolocation',
    label: 'Геолокация',
    visible: false,
    width: '150px',
    align: 'left',
    description: 'GPS-фиксация координат'
  },
  {
    id: 'geoUpdatedAt',
    label: 'Время GPS',
    visible: false,
    width: '140px',
    align: 'left',
    description: 'Дата последней фиксации координат'
  },
  {
    id: 'belongsStorage',
    label: 'Подчинение',
    visible: false,
    width: '160px',
    align: 'left',
    description: 'Подчиненные склады/аптеки'
  },
  {
    id: 'id',
    label: 'ID',
    visible: false,
    width: '80px',
    align: 'left',
    description: 'Системный ID пользователя'
  },
  {
    id: 'actions',
    label: 'Быстрый доступ',
    visible: true,
    fixed: 'end',
    width: '230px',
    align: 'right',
    description: 'Активация, касса, отчеты'
  }
];

interface StoredColumn {
  id: ColumnId;
  visible: boolean;
}

const loadInitialColumns = (): ColumnDef[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const stored = JSON.parse(raw) as StoredColumn[];
      if (Array.isArray(stored) && stored.length > 0) {
        const defMap = new Map<ColumnId, ColumnDef>(DEFAULT_COLUMNS.map(c => [c.id, c]));

        // Собираем промежуточные настраиваемые колонки строго в сохраненном порядке
        const configurableOrder: ColumnDef[] = [];
        const seen = new Set<ColumnId>();

        for (const item of stored) {
          if (item.id !== 'user' && item.id !== 'role' && item.id !== 'actions') {
            const def = defMap.get(item.id);
            if (def) {
              configurableOrder.push({
                ...def,
                visible: Boolean(item.visible)
              });
              seen.add(item.id);
            }
          }
        }

        // Добавляем отсутствующие колонки из дефолтного списка (если появились новые)
        for (const def of DEFAULT_COLUMNS) {
          if (def.id !== 'user' && def.id !== 'role' && def.id !== 'actions' && !seen.has(def.id)) {
            configurableOrder.push({ ...def });
          }
        }

        // Строгий порядок:
        // 1. Пользователь (всегда 1-й, всегда активен)
        // 2. Роль (всегда 2-я, всегда активна)
        // ... настраиваемые поля
        // N. Быстрый доступ (всегда последний, всегда активен)
        const userCol = { ...defMap.get('user')!, visible: true };
        const roleCol = { ...defMap.get('role')!, visible: true };
        const actionsCol = { ...defMap.get('actions')!, visible: true };

        return [userCol, roleCol, ...configurableOrder, actionsCol];
      }
    }
  } catch {
    // Хранилище недоступно или испорчено - показываем колонки по умолчанию
  }
  return DEFAULT_COLUMNS.map(c => ({ ...c }));
};

export const persistColumns = (columns: ColumnDef[]): void => {
  try {
    const toPersist: StoredColumn[] = columns.map(c => ({
      id: c.id,
      visible: c.fixed ? true : c.visible
    }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toPersist));
  } catch {
    // Настройка колонок - удобство: без хранилища она просто не переживет перезагрузку
  }
};

export interface UseTableColumnsReturn {
  columns: ColumnDef[];
  setColumns: React.Dispatch<React.SetStateAction<ColumnDef[]>>;
  saveColumns: (newColumns: ColumnDef[]) => void;
  resetToDefaults: () => void;
}

export const useTableColumns = (): UseTableColumnsReturn => {
  const [columns, setColumns] = useState<ColumnDef[]>(loadInitialColumns);

  const saveColumns = useCallback((newColumns: ColumnDef[]) => {
    // Гарантируем обязательные колонки:
    // [0] = user, [1] = role, [last] = actions
    const defMap = new Map<ColumnId, ColumnDef>(DEFAULT_COLUMNS.map(c => [c.id, c]));
    const userCol = { ...defMap.get('user')!, visible: true };
    const roleCol = { ...defMap.get('role')!, visible: true };
    const actionsCol = { ...defMap.get('actions')!, visible: true };

    const intermediate = newColumns.filter(c => c.id !== 'user' && c.id !== 'role' && c.id !== 'actions');
    const sanitized = [userCol, roleCol, ...intermediate, actionsCol];

    setColumns(sanitized);
    persistColumns(sanitized);
  }, []);

  const resetToDefaults = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    const defaults = DEFAULT_COLUMNS.map(c => ({ ...c }));
    setColumns(defaults);
  }, []);

  return {
    columns,
    setColumns,
    saveColumns,
    resetToDefaults
  };
};
